/**
 * Utility functions for Native Bridge
 */

import type { NativeError } from "./types"
import { ErrorCode, NativeBridgeError } from "./types"

export interface RetryOptions {
  maxAttempts?: number
  delay?: number
  backoff?: boolean
  shouldRetry?: (error: NativeError) => boolean
}

const DEFAULT_RETRY_OPTIONS: Required<RetryOptions> = {
  maxAttempts: 3,
  delay: 1000,
  backoff: true,
  shouldRetry: (error) => {
    // Retry on timeout and connection errors
    return [ErrorCode.TIMEOUT, ErrorCode.CONNECTION_FAILED, ErrorCode.NOT_CONNECTED].includes(error.code as ErrorCode)
  },
}

/**
 * Retry a function with exponential backoff
 */
export async function withRetry<T>(fn: () => Promise<T>, options?: RetryOptions): Promise<T> {
  const opts = { ...DEFAULT_RETRY_OPTIONS, ...options }
  let lastError: unknown

  for (let attempt = 1; attempt <= opts.maxAttempts; attempt++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error

      // Check if we should retry
      if (attempt === opts.maxAttempts) {
        break
      }

      if (error && typeof error === "object" && "code" in error) {
        if (!opts.shouldRetry(error as NativeError)) {
          throw error
        }
      }

      // Calculate delay with optional exponential backoff
      const delay = opts.backoff ? opts.delay * Math.pow(2, attempt - 1) : opts.delay

      await sleep(delay)
    }
  }

  throw lastError
}

/**
 * Sleep for specified milliseconds
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

/**
 * Create a timeout promise
 */
export function timeout<T>(promise: Promise<T>, ms: number, message?: string): Promise<T> {
  return Promise.race([
    promise,
    sleep(ms).then(() => {
      throw new NativeBridgeError(ErrorCode.TIMEOUT, message || `Operation timed out after ${ms}ms`)
    }),
  ])
}

/**
 * Debounce a function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number,
): (...args: Parameters<T>) => void {
  let timeoutId: NodeJS.Timeout

  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn(...args), delay)
  }
}

/**
 * Throttle a function
 */
export function throttle<T extends (...args: unknown[]) => unknown>(
  fn: T,
  limit: number,
): (...args: Parameters<T>) => void {
  let inThrottle = false

  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      fn(...args)
      inThrottle = true
      setTimeout(() => {
        inThrottle = false
      }, limit)
    }
  }
}

/**
 * Check if error is retryable
 */
export function isRetryableError(error: unknown): boolean {
  if (!error || typeof error !== "object" || !("code" in error)) {
    return false
  }

  const retryableCodes = [
    ErrorCode.TIMEOUT,
    ErrorCode.CONNECTION_FAILED,
    ErrorCode.NOT_CONNECTED,
    ErrorCode.DEVICE_NOT_FOUND,
  ]

  return retryableCodes.includes((error as { code: string }).code as ErrorCode)
}

/**
 * Format error for display
 */
export function formatError(error: unknown): string {
  if (!error) {
    return "Unknown error"
  }

  if (typeof error === "string") {
    return error
  }

  if (typeof error === "object") {
    if ("message" in error && typeof error.message === "string") {
      return error.message
    }
    if ("code" in error) {
      return `Error: ${(error as { code: string }).code}`
    }
  }

  return JSON.stringify(error)
}
